import { useState, Dispatch, SetStateAction } from 'react';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded'; // material
import {
  Tooltip,
  Stack,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import { useAuth } from '@calefy-inc/authentication';
import { LoadingIconButton } from './LoadingIconButton';
import { GenerateQuoteConfirmationButton } from './GenerateQuoteConfirmationButton';
import { SendRenewalEmailButton } from './SendRenewalEmailButton';
import { CopyRenewalLinkButton } from './CopyRenewalLinkButton';
// import { VagoDownloadDocumentsButton } from './VagoDownloadDocumentsButton';
import { VagoForceReRateButton } from './VagoForceReRateButton';
import { SpireDocumentDownloadButton } from './SpireDocumentDownloadButton';
import { PythonDocxDocumentDownloadButton } from './PythonDocxDocumentDownloadButton';
import { useUsingVagoSettings } from '../../../../../../hooks';

// types and classes
import { Quote } from '../../../../../../Typescript/backend/classes';
import type { $TSFixMe } from '@calefy-inc/utilityTypes';
import { VagoFileDownloadMenuButton } from '../../../../../OrganizationSpecific/Vago';
import { PolicyStreamFileDownloadMenuButton } from '../../../../../PolicyStream/components/PolicyStreamDownloadMenuButton';
import { VagoResendSubmissionEmailButton } from './VagoResendSubmissionEmailButton';
import { VagoClaimsButton } from './VagoClaimsButton';
import { VagoInitiateRenewalButton } from './VagoInitiateRenewalButton';

// ----------------------------------------------------------------------
interface QuoteToolbarProps {
  quote: Quote | null;
  setStatus: Dispatch<SetStateAction<Quote['status'] | null>>;
  onDownload: $TSFixMe;
  pdfStatus: $TSFixMe;
  selectedRevision: $TSFixMe;
  revisionList: $TSFixMe;
  handleRevisionChange: $TSFixMe;
  isArchived: $TSFixMe;
  handleRenewalOnlyChange: $TSFixMe;
  renewalOnly: $TSFixMe;
}

export default function QuoteToolbar({
  quote,
  setStatus,
  onDownload,
  pdfStatus,
  selectedRevision,
  revisionList,
  handleRevisionChange,
  isArchived,
  handleRenewalOnlyChange,
  renewalOnly,
}: QuoteToolbarProps) {
  // @ts-expect-error
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [openPDF, setOpenPDF] = useState<boolean>(false);
  const { token } = useAuth();
  const onVago = useUsingVagoSettings();

  if (!quote) {
    return null;
  }
  if (!token) {
    return null;
  }

  return (
    <>
      <Stack
        mb={3}
        direction='row'
        justifyContent='space-between'
        alignItems='flex-end'
        spacing={1.5}
      >
        <Box sx={{ minWidth: 120, width: 250 }}>
          {revisionList.length > 0 ? (
            <FormControl fullWidth size='small'>
              <InputLabel id='revision-selector'>Revisions</InputLabel>
              <Select
                labelId='revision-selector-label'
                id='revision-selector'
                value={selectedRevision}
                label='Revisions'
                onChange={handleRevisionChange}
              >
                <MenuItem value={'0'}>Current revision</MenuItem>
                {
                  // @ts-expect-error
                  revisionList.map((revision, index: number) => (
                    <MenuItem value={revision.quoteId} key={index}>{`R${
                      revisionList.length - index
                    } - ${revision.dateAdded}`}</MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          ) : null}
        </Box>
        <Stack direction='row' alignItems='center' spacing={1.5}>
          <VagoResendSubmissionEmailButton quote={quote} />
          <VagoInitiateRenewalButton quote={quote} />
          <VagoClaimsButton quote={quote} />
          <PythonDocxDocumentDownloadButton
            quote={quote}
            renewalOnly={renewalOnly}
            selectedRevision={selectedRevision}
          />
          <SpireDocumentDownloadButton
            quote={quote}
            renewalOnly={renewalOnly}
            selectedRevision={selectedRevision}
          />
          <VagoForceReRateButton quote={quote} />
          {/* <VagoDownloadDocumentsButton quote={quote} /> */}
          <GenerateQuoteConfirmationButton quote={quote} />
          {!quote.isPolicyStreamApplication ? (
            <CopyRenewalLinkButton
              uuid={quote.uniqueId}
              setStatus={setStatus}
              disabled={!(quote.isComplete() || quote.isReadyToRenew())}
            />
          ) : null}
          {!quote.isPolicyStreamApplication &&
          (quote.status === 'RENEWED' ||
            quote.status === 'INCOMPLETE_RENEWAL' ||
            quote.status === 'RENEWAL_IN_PROGRESS') ? (
            <Tooltip
              title={
                !renewalOnly
                  ? 'Show renewal questions only'
                  : 'Show all questions '
              }
            >
              <IconButton
                size='small'
                aria-label='renewal questions only'
                onClick={handleRenewalOnlyChange}
              >
                {!renewalOnly ? (
                  <VisibilityRoundedIcon fontSize='small' />
                ) : (
                  <VisibilityOffRoundedIcon fontSize='small' />
                )}
              </IconButton>
            </Tooltip>
          ) : null}

          <Tooltip
            title={
              quote.anyBound()
                ? 'Quote has been bound; no further edits are possible'
                : quote.isComplete()
                ? 'Edit warning: Application has been completed'
                : 'Edit'
            }
          >
            <span>
              <IconButton
                aria-label={
                  quote.anyBound()
                    ? 'Quote has been bound; no further edits are possible'
                    : quote.isComplete()
                    ? 'Edit warning: Application has been completed'
                    : 'Edit'
                }
                data-testid='edit_quote_button'
                size='small'
                href={
                  quote.isPolicyStreamApplication
                    ? `/insurance/resume/${quote.uniqueId}`
                    : `/insurtech/quote/resume/${quote.uniqueId}`
                }
                disabled={isArchived || quote.anyBound()}
              >
                <EditRoundedIcon fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>

          <SendRenewalEmailButton
            quote={quote}
            setStatus={setStatus}
            isArchived={isArchived}
          />

          <Tooltip title='Download'>
            {quote.isPolicyStreamApplication ? (
              <PolicyStreamFileDownloadMenuButton quote={quote} />
            ) : onVago ? (
              <VagoFileDownloadMenuButton quote={quote} />
            ) : (
              <LoadingIconButton
                size='small'
                aria-label='download'
                loading={pdfStatus}
                onClick={() => onDownload()}
                loadingPosition='center'
              >
                <DownloadRoundedIcon fontSize='small' />
              </LoadingIconButton>
            )}
          </Tooltip>
        </Stack>
      </Stack>
    </>
  );
}
